import { useIntl } from "react-intl";

import config from "console_config";
import { selectZendeskLinksEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { meSelector } from "Reducers/app/selectors";
import { useAppSelector } from "Store/hooks";

import { buildSupportUrl } from "./buildSupportUrl";

const ZENDESK_SUBMIT_TICKET_FORM_ID = config.ZENDESK_SUBMIT_TICKET_FORM_ID;
const ZENDESK_FEATURE_REQUEST_FORM_ID = config.ZENDESK_FEATURE_REQUEST_FORM_ID;
export const ZENDESK_DELETE_ACCOUNT_FORM_ID =
  config.ZENDESK_DELETE_ACCOUNT_FORM_ID;

type ExternalLinkProps = {
  href: string;
  target: string;
  rel: string;
};

type InternalLinkProps = {
  to: string;
};

export type SupportLinkProps = ExternalLinkProps | InternalLinkProps;

/**
 * Returns support link properties based on the vendor configuration.
 *
 * - Zendesk enabled (`ENABLE_ZENDESK_LINKS: true`): external `href` links to Zendesk.
 * - Custom support URL set (`CUSTOM_URL_SUPPORT`): external `href` links to the vendor portal.
 * - Neither: internal `to` links to in-app ticket pages.
 */
export function useSupportLinks() {
  const isZendeskFlag = useAppSelector(selectZendeskLinksEnabled);
  const me = useAppSelector(meSelector);
  const intl = useIntl();

  const username = me?.username ?? "-";
  const ticketsBase = `/-/users/${username}/tickets`;
  const hasCustomSupportUrl = Boolean(config.CUSTOM_URL_SUPPORT);
  // isZendesk is driven by the ENABLE_ZENDESK_LINKS feature flag.
  // Vendors with their own support portal (e.g. Adobe/Magento) set
  // ENABLE_ZENDESK_LINKS: false and keep CUSTOM_URL_SUPPORT pointing
  // at their portal (e.g. Experience League).
  const isZendesk = isZendeskFlag;

  const supportLink = (
    path?: string,
    params?: Record<string, string>
  ): SupportLinkProps => {
    if (isZendesk) {
      const zendeskParams = { ...params };
      if (path === "requests/new" && !zendeskParams.ticket_form_id) {
        zendeskParams.ticket_form_id = ZENDESK_SUBMIT_TICKET_FORM_ID;
      }
      return {
        href: buildSupportUrl(path, zendeskParams),
        target: "_blank",
        rel: "noopener noreferrer"
      };
    }

    // When CUSTOM_URL_SUPPORT is set, always link externally to the vendor's
    // support page (e.g. Adobe Experience League). The in-app ticket system is
    // not available for these vendors — /tickets redirects to / when
    // CUSTOM_URL_SUPPORT is configured.
    if (hasCustomSupportUrl) {
      return {
        href: config.CUSTOM_URL_SUPPORT,
        target: "_blank",
        rel: "noopener noreferrer"
      };
    }

    const inAppPath =
      path === "requests/new" ? `${ticketsBase}/open` : ticketsBase;

    if (params) {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        // Convert Zendesk tf_ prefixed params back to in-app params
        const cleanKey = key.replace(/^tf_/, "");
        searchParams.set(cleanKey, value);
      }
      return { to: `${inAppPath}?${searchParams.toString()}` };
    }

    return { to: inAppPath };
  };

  const submitIdeaLink = (): SupportLinkProps => {
    if (isZendesk) {
      return {
        href: buildSupportUrl("requests/new", {
          ticket_form_id: ZENDESK_FEATURE_REQUEST_FORM_ID,
          tf_subject: intl.formatMessage({ id: "submit_an_idea.subject" }),
          tf_description: intl.formatMessage({
            id: "submit_an_idea.message"
          })
        }),
        target: "_blank",
        rel: "noopener noreferrer"
      };
    }

    if (hasCustomSupportUrl) {
      return {
        href: config.CUSTOM_URL_SUPPORT,
        target: "_blank",
        rel: "noopener noreferrer"
      };
    }

    const params = new URLSearchParams({
      category: "feature_request_cat",
      priority: "low",
      subject: intl.formatMessage({ id: "submit_an_idea.subject" }),
      description: intl.formatMessage({ id: "submit_an_idea.message" }),
      isGeneral: "true"
    });

    return { to: `${ticketsBase}/open?${params.toString()}` };
  };

  return {
    isZendesk,
    hasCustomSupportUrl,
    supportLink,
    submitIdeaLink,
    supportPageUrl: isZendesk
      ? buildSupportUrl()
      : hasCustomSupportUrl
        ? config.CUSTOM_URL_SUPPORT
        : ticketsBase
  };
}

export function getSupportLinkUrl(props: SupportLinkProps): string {
  return "href" in props ? props.href : props.to;
}
